/**
 * The journal's client panel (boona docs/codebase/clients/README.md §3.6, plan CL3b): what the
 * dashboard client card shows at a glance - the pinned alert, state and categories, the visit
 * rhythm (last visit, usual interval, booked or due), visits and no-shows, packages left,
 * birthday - and "Open card". Read from GET /api/v1/company/clients/{id}/summary with the
 * Clients rights: a role without them gets a 403 and the panel stays away.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { AlertOctagon, CalendarCheck, CalendarClock, Cake, ExternalLink, Package } from "lucide-react"
import { apiClient } from "@/lib/apiClient"
import { goToDashboard } from "@/features/sso/goToDashboard"

type ClientSummary = {
  id: number
  state: "never" | "new" | "active" | "due" | "risk" | "lost"
  alert: string | null
  categories: { title: string; color: string | null }[]
  visits: number
  no_shows: number
  cancels: number
  last: string | null
  interval: number | null
  due: string | null
  overdue: number | null
  next: string | null
  packages: { title: string; left: number | null; total: number | null; unlimited: boolean; valid_until: string | null }[]
  birth_date: string | null
  consent: { granted: boolean; at: string | null } | null
  card_path: string
}

const STATE_TONE: Record<ClientSummary["state"], string> = {
  never: "bg-white text-gray-500 ring-1 ring-gray-200",
  new: "bg-blue-50 text-blue-700",
  active: "bg-emerald-50 text-emerald-700",
  due: "bg-white text-amber-700 ring-1 ring-amber-200",
  risk: "bg-amber-50 text-amber-700",
  lost: "bg-gray-100 text-gray-500",
}

/** "1990-05-01" / "05-01" → "01.05" (+ year); anything else as it is. */
function birthday(value: string | null): string | null {
  if (!value) return null
  const full = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (full) return `${full[3]}.${full[2]}.${full[1]}`
  const short = /^(\d{2})-(\d{2})$/.exec(value)
  return short ? `${short[2]}.${short[1]}` : value
}

export function ClientSummaryPanel({ clientId, recordId }: { clientId: number | null; recordId?: number | null }) {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  // saved at once to the client's consent log; record_id also puts the change into this
  // visit's history (owner 2026-10-10), so the Changes tab and the activity feed refetch
  const consent = useMutation({
    mutationFn: async (granted: boolean) =>
      (await apiClient.post(`/api/v1/company/clients/${clientId}/consent`, { granted, record_id: recordId || null })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clientSummary", clientId] })
      if (recordId) queryClient.invalidateQueries({ queryKey: ["record-revisions", recordId] })
      queryClient.invalidateQueries({ queryKey: ["revisions"] })
    },
  })
  const { data } = useQuery({
    queryKey: ["clientSummary", clientId],
    enabled: !!clientId,
    queryFn: async () => (await apiClient.get<ClientSummary>(`/api/v1/company/clients/${clientId}/summary`)).data,
    staleTime: 60_000,
    retry: false,
  })
  if (!clientId || !data) return null

  const day = (iso: string | null) =>
    iso ? new Intl.DateTimeFormat(i18n.language, { day: "2-digit", month: "2-digit" }).format(new Date(iso)) : "—"
  const states: Record<ClientSummary["state"], string> = {
    never: t("appointment.client.state.never", "Never came"),
    new: t("appointment.client.state.new", "New"),
    active: t("appointment.client.state.active", "Active"),
    due: t("appointment.client.state.due", "Due soon"),
    risk: t("appointment.client.state.risk", "At risk"),
    lost: t("appointment.client.state.lost", "Lost"),
  }
  const born = birthday(data.birth_date)

  return (
    <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4 text-sm">
      {data.alert && (
        <div className="flex gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-rose-800">
          <AlertOctagon className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="whitespace-pre-line">{data.alert}</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATE_TONE[data.state]}`}>{states[data.state]}</span>
        {data.categories.map((c) => (
          <span key={c.title} className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-0.5 text-xs font-semibold text-gray-700">
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: c.color || "#9aa0a8" }} />
            {c.title}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div>
          <div className="text-xs text-gray-500">{t("appointment.client.summary.visits", "Visits")}</div>
          <div className="font-semibold">{data.visits}</div>
          {data.no_shows > 0 && (
            <div className="text-xs text-rose-700">{t("appointment.client.summary.no_shows", "{{count}} no-shows", { count: data.no_shows })}</div>
          )}
        </div>
        <div>
          <div className="text-xs text-gray-500">{t("appointment.client.summary.interval", "Usual interval")}</div>
          <div className="font-semibold">
            {data.interval ? t("appointment.client.summary.days", "{{count}} d", { count: data.interval }) : "—"}
          </div>
          <div className="text-xs text-gray-500">{t("appointment.client.summary.last", "last {{date}}", { date: day(data.last) })}</div>
        </div>
        <div>
          {data.next ? (
            <>
              <div className="text-xs text-gray-500">{t("appointment.client.summary.next", "Next visit")}</div>
              <div className="flex items-center gap-1 font-semibold text-emerald-700">
                <CalendarCheck className="h-3.5 w-3.5" />
                {day(data.next)}
              </div>
            </>
          ) : (
            <>
              <div className="text-xs text-gray-500">{t("appointment.client.summary.due", "Next due")}</div>
              <div className={`flex items-center gap-1 font-semibold ${data.overdue ? "text-amber-700" : ""}`}>
                {data.due && <CalendarClock className="h-3.5 w-3.5" />}
                {day(data.due)}
              </div>
              {data.overdue ? (
                <div className="text-xs text-amber-700">{t("appointment.client.summary.overdue", "{{count}} d overdue", { count: data.overdue })}</div>
              ) : null}
            </>
          )}
        </div>
      </div>

      {data.packages.length > 0 && (
        <div className="space-y-1">
          {data.packages.map((p) => (
            <div key={p.title} className="flex items-center gap-2 text-xs">
              <Package className="h-3.5 w-3.5 text-violet-500" />
              <span className="font-medium">{p.title}</span>
              <span className="text-gray-500">
                {p.unlimited
                  ? t("appointment.client.summary.unlimited", "unlimited")
                  : t("appointment.client.summary.left", "{{count}} left", { count: p.left ?? 0 })}
              </span>
            </div>
          ))}
        </div>
      )}

      {born && (
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <Cake className="h-3.5 w-3.5" />
          {born}
        </div>
      )}

      {/* marketing consent (boona clients D11): offers need a yes; booking reminders do not */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-gray-500">{t("appointment.client.summary.offers", "Offers")}:</span>
        <span className={data.consent?.granted ? "font-medium text-emerald-700" : data.consent ? "font-medium text-rose-700" : "text-gray-500"}>
          {data.consent?.granted
            ? t("appointment.client.summary.consent_yes", "agreed")
            : data.consent
              ? t("appointment.client.summary.consent_no", "refused")
              : t("appointment.client.summary.consent_unknown", "not asked")}
        </span>
        {!data.consent?.granted && (
          <button type="button" disabled={consent.isPending} onClick={() => consent.mutate(true)}
            className="rounded border border-gray-200 px-1.5 py-0.5 hover:bg-gray-50">
            {t("appointment.client.summary.consent_set_yes", "Agreed")}
          </button>
        )}
        {(data.consent === null || data.consent?.granted) && (
          <button type="button" disabled={consent.isPending} onClick={() => consent.mutate(false)}
            className="rounded border border-gray-200 px-1.5 py-0.5 hover:bg-gray-50">
            {t("appointment.client.summary.consent_set_no", "Refused")}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => void goToDashboard(data.card_path)}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:underline"
      >
        <ExternalLink className="h-3.5 w-3.5" />
        {t("appointment.client.summary.open_card", "Open client card")}
      </button>
    </div>
  )
}
